import { useState } from "react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { Mail, Send } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll reply within one business day.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch { toast.error("Something went wrong. Try again in a moment."); }
    finally { setBusy(false); }
  };

  return (
    <>
      <SEO pageKey="contact" title="Contact — SerpNex" description="Have a question about SerpNex? Email contact@serpnex.com or use the form. We reply within one business day." canonical="/contact" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 grid md:grid-cols-2 gap-14">
        <div>
          <div className="eyebrow">Contact</div>
          <h1 className="mt-3 h-display text-5xl font-bold text-[#0A0D14]">Talk to a human.</h1>
          <p className="mt-4 text-[#525B6A]">Sales, support, a feature request, or a wild idea — don&apos;t hesitate to share any thoughts, feedback, or questions, big or small. We reply within one business day.</p>
          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-3 text-sm text-[#0A0D14]">
              <Mail className="h-4 w-4 text-[#0047FF]" /> contact@serpnex.com
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="card-crisp p-8 space-y-4" data-testid="contact-form">
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Name</label>
            <input required data-testid="contact-name" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Email</label>
            <input required type="email" data-testid="contact-email" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Company (optional)</label>
            <input data-testid="contact-company" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Message</label>
            <textarea required rows="5" data-testid="contact-message" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <button disabled={busy} data-testid="contact-submit" className="btn-primary w-full px-4 py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2">
            {busy ? "Sending…" : (<>Send message <Send className="h-4 w-4" /></>)}
          </button>
        </form>
      </div>
    </>
  );
}
